import { AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { RestrictedField } from '@/features/guardian/restricted-field';
import { useGuardianAccessLevelForStudent } from '@/features/guardian/hooks';
import { useHasCapability } from '@/lib/capabilities';
import { CompleteSessionDialog } from '@/features/murajaah/complete-session-dialog';
import { useCancelSession, useMurajaahSessions } from '@/features/murajaah/hooks';
import {
  SESSION_MISSED_LABEL,
  SESSION_STATUS_LABELS,
  SESSION_TYPE_LABELS,
} from '@/features/murajaah/labels';
import { ScheduleSessionDialog } from '@/features/murajaah/schedule-session-dialog';
import type { MurajaahSessionView } from '@/features/murajaah/types';

const PAGE_SIZE = 20;

function isMissed(session: MurajaahSessionView): boolean {
  return (
    session.status === 'SCHEDULED' &&
    session.completedAt === null &&
    new Date(session.scheduledAt).getTime() < Date.now()
  );
}

function statusLabel(session: MurajaahSessionView): string {
  return isMissed(session) ? SESSION_MISSED_LABEL : SESSION_STATUS_LABELS[session.status];
}

function statusVariant(session: MurajaahSessionView): 'default' | 'secondary' | 'destructive' {
  if (session.status === 'COMPLETED') {
    return 'default';
  }
  if (session.status === 'CANCELLED' || isMissed(session)) {
    return 'destructive';
  }
  return 'secondary';
}

/** Same icon-plus-highlight language target-list.tsx's StatusIcon introduced (Phase 8, Pilar C). */
function StatusIcon({ session }: { readonly session: MurajaahSessionView }): JSX.Element {
  const className = 'h-4 w-4 shrink-0';

  if (session.status === 'COMPLETED') {
    return <CheckCircle2 className={`${className} text-primary`} aria-hidden="true" />;
  }
  if (session.status === 'CANCELLED') {
    return <XCircle className={`${className} text-muted-foreground`} aria-hidden="true" />;
  }
  if (isMissed(session)) {
    return <AlertCircle className={`${className} text-destructive`} aria-hidden="true" />;
  }
  return <Clock className={`${className} text-muted-foreground`} aria-hidden="true" />;
}

/**
 * Murajaah session history (Phase G-2, Job 18). `notes` is genuinely
 * optional at write time, so a `null` value alone cannot tell redaction
 * apart from "no notes were written" - `useGuardianAccessLevelForStudent`
 * resolves that from the caller's own ACTIVE Guardian link, the same
 * source of truth the backend itself redacts from.
 *
 * While that resolution is still loading or has failed (Phase G-5, Job
 * 13 - re-investigating a cosmetic gap G-2's independent review had
 * already documented), a `null` `notes` field is genuinely ambiguous: it
 * could mean "no notes" or "restricted, but we don't know that yet." A
 * per-row skeleton is shown instead of committing to either reading -
 * this never widens what data is shown (the redacted value was already
 * `null` from the backend either way), it only avoids the brief,
 * misleading "no notes were written" label a LIMITED_READ Guardian could
 * otherwise see for a fraction of a second on first load.
 */
export function SessionList({ studentId }: { readonly studentId: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [completingSession, setCompletingSession] = useState<MurajaahSessionView | null>(null);
  const { data, isLoading, isError } = useMurajaahSessions(studentId, page, PAGE_SIZE);
  const {
    accessLevel,
    isLoading: accessLevelLoading,
    isError: accessLevelError,
  } = useGuardianAccessLevelForStudent(studentId);
  const accessLevelUnresolved = accessLevelLoading || accessLevelError;
  const cancel = useCancelSession(studentId);
  const canManage = useHasCapability('murajaah_sessions:manage');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Murajaah</CardTitle>
        {canManage && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setScheduleOpen(true);
            }}
          >
            Jadwalkan Murajaah
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat murajaah.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada murajaah.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <>
            <ul className="flex flex-col gap-2">
              {data.items.map((session) => {
                const notesAmbiguous = session.notes === null && accessLevelUnresolved;
                const isRedacted = accessLevel === 'LIMITED_READ' && session.notes === null;
                return (
                  <li
                    key={session.id}
                    className={`flex flex-col gap-2 rounded-md border p-3 ${
                      session.status === 'COMPLETED'
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border'
                    }`}
                  >
                    <div className="flex flex-row items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <StatusIcon session={session} />
                        {SESSION_TYPE_LABELS[session.sessionType]} ·{' '}
                        {new Date(session.scheduledAt).toLocaleString('id-ID')}
                      </span>
                      <Badge variant={statusVariant(session)}>{statusLabel(session)}</Badge>
                    </div>
                    {session.completedAt !== null && (
                      <span className="text-xs text-muted-foreground">
                        Selesai {new Date(session.completedAt).toLocaleString('id-ID')}
                      </span>
                    )}
                    {notesAmbiguous ? (
                      <Skeleton className="h-4 w-32" />
                    ) : isRedacted ? (
                      <RestrictedField value={null} render={() => null} className="text-xs" />
                    ) : (
                      session.notes !== null && (
                        <p className="text-xs text-muted-foreground">Catatan: {session.notes}</p>
                      )
                    )}
                    {canManage && session.status === 'SCHEDULED' && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCompletingSession(session);
                          }}
                        >
                          Selesaikan
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button type="button" size="sm" variant="destructive">
                              Batalkan
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Batalkan sesi murajaah ini?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Sesi akan ditandai dibatalkan. Tindakan ini tidak dapat dibatalkan
                                dari dalam aplikasi.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction
                                disabled={cancel.isPending}
                                onClick={() => {
                                  cancel.mutate(session.id);
                                }}
                              >
                                Batalkan Sesi
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {data.total_pages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => Math.max(1, current - 1));
                  }}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-muted-foreground">
                  Halaman {data.page} dari {data.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= data.total_pages}
                  onClick={() => {
                    setPage((current) => Math.min(data.total_pages, current + 1));
                  }}
                >
                  Berikutnya
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <ScheduleSessionDialog
        studentId={studentId}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
      />
      {completingSession !== null && (
        <CompleteSessionDialog
          studentId={studentId}
          session={completingSession}
          open={completingSession !== null}
          onOpenChange={(next) => {
            if (!next) {
              setCompletingSession(null);
            }
          }}
        />
      )}
    </Card>
  );
}
